import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { Outlet } from '@tanstack/react-router';
import { useGetAllShipmentMetaData, useMetaData } from '@uturn/api/v1';
import { useFetchOrganizationStatus } from '@uturn/api/v3';
import { rules } from '@uturn/portal/abac';
import { Usetiful } from '@uturn/portal/services';
import { usePostHog } from 'posthog-js/react';
import { Suspense, useEffect } from 'react';
import { AbacProvider } from 'react-abac';
import { useLocale } from 'react-aria';
import { useTranslation } from 'react-i18next';
import { LoadingSpinnerWithLogo } from './components';
import FinancialBlockBanner from './components/financial-block-banner';
import { Header } from './components/header';
import { SearchProvider } from './components/search-provider';
import { TermsAndConditionsModal } from './components/terms-and-conditions-modal';
import { getMainNavigation, getUserNavigation } from './config/navigation';
import { MetaDataContext } from './context';
import { FinancialBlockProvider } from './context/financial-block-context';
import { MeiliSearchProvider } from './meilisearch';
import { useCarrierStore } from './store/carrier';
import { useArchivedShipments } from './store/shipments/archived-shipment';
import { useAssignedShipmentStore } from './store/shipments/assigned-shipment';
import { useDraftedShipments } from './store/shipments/drafted-shipment';
import { usePublishedShipments } from './store/shipments/published-shipment';

export default function AppLayout() {
	const { t } = useTranslation();
	const posthog = usePostHog();

	// Get base navigation and add preFetch callbacks
	const navigation = getMainNavigation(t).map((item) => {
		if (item.href === '/shipments') {
			return {
				...item,
				preFetch: () => {
					usePublishedShipments.getState().fetch();
					useAssignedShipmentStore.getState().fetch();
					useDraftedShipments.getState().fetch();
					useArchivedShipments.getState().fetch();
				},
			};
		}
		if (item.href === '/carriers') {
			return {
				...item,
				preFetch: () => {
					useCarrierStore.getState().fetch();
				},
			};
		}
		return item;
	});

	const { data: metaData } = useMetaData({
		query: {
			refetchOnWindowFocus: false,
			select: (data) => data.data,
		},
	});

	const user = metaData?.user;

	const { data: shipmentMetaData } = useGetAllShipmentMetaData({
		query: {
			refetchOnWindowFocus: false,
			select: (data) => data.data,
		},
	});
	const { data: shipmentUnits } = useQuery({
		queryKey: ['shipmentUnits'],
		queryFn: async () => {
			const response = await fetch('/shipment-units.json');
			const data = await response.json();
			return data;
		},
		gcTime: Infinity,
		staleTime: Infinity,
	});
	const orgId = user?.organisation?.id ?? 0;

	const { data: organisationStatus } = useFetchOrganizationStatus(orgId, {
		query: {
			enabled: !!orgId,
		},
	});

	const isFinancialBlocked =
		organisationStatus?.data.status?.code === 'FINANCIAL_BLOCK' || false;

	// biome-ignore lint/plugin/no-use-effect: keeps external identity in sync with metadata changes
	useEffect(() => {
		if (user?.id === undefined) {
			return;
		}

		posthog.identify(user.id.toString(), {
			...user,
			orgName: user.organisation?.name ?? null,
			orgId: user.organisation?.id ?? null,
		});

		// Associate user with an organization group
		if (user.organisation?.id) {
			posthog.group('organization', user.organisation.id.toString(), {
				group_name: user.organisation.name,
				group_id: user.organisation.id,
			});

			// ✅ Capture Group-Level Properties
			posthog.capture('organization_associated', {
				group_type: 'organization',
				group_id: user.organisation.id,
				group_name: user.organisation.name,
			});
		}
		Usetiful.identify(user);
		Sentry.setUser({
			id: user.id.toString(),
			email: user.username,
			fullName: `${user.firstName ?? ''} ${user.surname ?? ''}`,
			role: user.role ?? null,
			orgId: user.organisation?.id ?? null,
			orgName: user.organisation?.name ?? null,
			associationStatus: user.associationStatus ?? null,
		});
		Sentry.setTag('user_role', user.role ?? 'none');
		Sentry.setTag('org_id', user.organisation?.id?.toString() ?? 'none');
		Sentry.setTag('association_status', user.associationStatus ?? 'none');
	}, [user, posthog]);

	const { locale, direction } = useLocale();

	return (
		<FinancialBlockProvider isFinancialBlocked={isFinancialBlocked}>
			<MetaDataContext.Provider
				value={{
					metadata: metaData,
					shipmentMetaData,
					shippingRequirementExceptions: ['ADR', 'GENSET'],
					shippingCarrierRelationStatuses: ['Dedicated', 'Contracted'],
					shipmentUnits,
					shipmentMaxRoutes: 5,
				}}
			>
				<AbacProvider user={user} roles={[user?.role ?? '']} rules={rules}>
					<MeiliSearchProvider>
						<SearchProvider>
							<div className="h-full" lang={locale} dir={direction}>
								<Header
									userNavigation={navigation}
									dropdownItems={getUserNavigation(
										user?.organisation?.id as number,
										t,
									)}
									user={{
										name: `${user?.firstName} ${user?.surname}`,
										role: user?.role?.includes('ADMIN') ? 'Admin' : 'User',
									}}
								/>
								<div className="3xl:w-2/3 3xl:px-0 m-auto px-4 lg:px-8">
									{isFinancialBlocked && (
										<FinancialBlockBanner className="mt-4" />
									)}

									<Suspense fallback={<LoadingSpinnerWithLogo />}>
										<Outlet />
									</Suspense>
								</div>
								<TermsAndConditionsModal />
							</div>
						</SearchProvider>
					</MeiliSearchProvider>
				</AbacProvider>
			</MetaDataContext.Provider>
		</FinancialBlockProvider>
	);
}
